import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/_core/hooks/useAuth';
import {
  canAccessPortal,
  defaultPortalPath,
  isDualRole,
  SELECT_PORTAL_PATH,
  type PortalKind,
} from '@/utils/portalRouting';

export default function PortalGate({
  portal,
  children,
}: {
  portal: PortalKind;
  children: ReactNode;
}) {
  const { user, loading, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (loading || !isAuthenticated || !user) return;
    if (canAccessPortal(user, portal)) return;
    const dest = isDualRole(user) ? SELECT_PORTAL_PATH : defaultPortalPath(user);
    navigate(dest, { replace: true });
  }, [loading, isAuthenticated, user, portal, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && user && !canAccessPortal(user, portal)) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}
